import { useHead } from '@unhead/react';
import { Card, Col, Descriptions, Flex, Modal, Row } from 'antd';
import { Typography } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import Routes from 'config/routes';
import { formatDateWithMiddleDot } from 'technical/format/date';
import { useMediaType } from 'technical/media/hooks';
import { PreviousPageButton } from 'ui/button/previous-page-button';
import CapCard from 'ui/cap-card';
import SectionTitle from 'ui/title/sectionTitle';

import { FileUploadForm } from './components/file-upload-form';
import { useImportConfData } from './hooks/data/use-import-conf';
import {
  OngoingTask,
  useOngoingImportTaskData,
} from './hooks/data/use-ongoing-import-task';
import style from './index.module.scss';

export function SegmentImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useMediaType();

  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  useHead({
    title: t('qualityControl.segmentImport.title'),
  });

  const { lastImportTask, ongoingTask, setOngoingTask } =
    useOngoingImportTaskData(currentConstructionSite.projectId);

  const { columns, extraColumns } = useImportConfData(
    currentConstructionSite.projectId,
  );

  const updateOngoingTask = (task: OngoingTask) => {
    setOngoingTask(task);
  };

  return (
    <Flex
      vertical
      flex={1}
      gap={20}
      align="center"
      className={classNames(style.page, 'page-appear')}
    >
      <SectionTitle
        className={style.backButton}
        leftButton={
          <PreviousPageButton
            to={{ pathname: Routes.QualitySegmentHistory }}
            label="qualityControl.segmentImport.cta.goBack"
            isMobile={isMobile}
          />
        }
      />
      <CapCard
        title={t('qualityControl.segmentImport.subTitle')}
        className={style.card}
      >
        <Row gutter={24}>
          <Col xs={24} md={10}>
            <Card className={style.detailsCard}>
              <p className={style.lastUpdate}>
                {t('qualityControl.segmentImport.details.lastUpdateTitle', {
                  date: lastImportTask?.endDate
                    ? formatDateWithMiddleDot(lastImportTask.endDate)
                    : '-',
                })}
              </p>

              <ul className={style.statsList}>
                <li>
                  {t('qualityControl.segmentImport.details.segmentAdded', {
                    nbSegmentAdded: lastImportTask
                      ? lastImportTask.segmentCreated
                      : '-',
                  })}
                </li>
                <li>
                  {t('qualityControl.segmentImport.details.segmentUpdated', {
                    nbSegmentUpdated: lastImportTask
                      ? lastImportTask.segmentUpdated
                      : '-',
                  })}
                </li>
                <li>
                  {t('qualityControl.segmentImport.details.segmentWarning', {
                    nbSegmentErrors: lastImportTask
                      ? lastImportTask.segmentFailed
                      : '-',
                  })}
                </li>
                {lastImportTask?.warnings?.type === 'global' ? (
                  <li>
                    {t('qualityControl.segmentImport.details.segmentError')}
                  </li>
                ) : null}
                {lastImportTask?.warnings ? (
                  <Typography.Link
                    className={style.errorLink}
                    onClick={() =>
                      navigate(
                        `${Routes.QualitySegmentImportWarnings}?${createSearchParams({ id: lastImportTask.id })}`,
                      )
                    }
                  >
                    {t(
                      'qualityControl.segmentImport.details.displayImportErrors',
                    )}
                  </Typography.Link>
                ) : null}
              </ul>

              <div>{t('qualityControl.segmentImport.details.totalTitle')}</div>
              <p className={style.totalSegment}>
                {t('qualityControl.segmentImport.details.total', {
                  nbSegments: lastImportTask
                    ? lastImportTask.segmentCreated +
                      lastImportTask.segmentUpdated +
                      lastImportTask.segmentFailed
                    : '-',
                })}
              </p>
            </Card>
          </Col>

          <Col xs={24} md={14}>
            <Row justify={'center'}>
              <FileUploadForm
                ongoingTask={ongoingTask}
                setOngoingTask={updateOngoingTask}
              />
              <Typography.Link
                className={style.customLink}
                onClick={() => setIsInfoModalOpen(true)}
              >
                {t(
                  'qualityControl.segmentImport.details.displayExpectedColumns',
                )}
              </Typography.Link>
            </Row>
          </Col>
        </Row>
      </CapCard>
      <Modal
        title={t('qualityControl.segmentImport.details.expectedColumns')}
        open={isInfoModalOpen}
        onOk={() => setIsInfoModalOpen(false)}
        onCancel={() => setIsInfoModalOpen(false)}
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item
            label={t('qualityControl.segmentImport.details.fileNameHeader')}
            key={t('qualityControl.segmentImport.details.fileNameHeader')}
            style={{ fontWeight: 'bold', backgroundColor: 'white' }}
          >
            {t('qualityControl.segmentImport.details.displayNameHeader')}
          </Descriptions.Item>
          {columns.map((item) => (
            <Descriptions.Item label={item.label} key={item.label}>
              {t(item.definition)}
            </Descriptions.Item>
          ))}
        </Descriptions>
        <h4>
          {t('qualityControl.segmentImport.details.expectedExtraColumns')}
        </h4>
        <Descriptions column={1} bordered>
          {extraColumns.map((item) => (
            <Descriptions.Item label={item.label} key={item.label}>
              {item.definition}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Modal>
    </Flex>
  );
}
